import axios from 'axios';
import { cacheAdapterEnhancer } from 'axios-extensions';
import qs from 'qs';
import i18n from 'i18next';
import Mock from 'mockjs';
import { isDebug, isProjectUrl, flatObject } from '../utils';
import logger from '../utils/logger';
import { isQuietMode, isDevelopment } from '../utils/envHelper';
import config from './config';
import { getGhostUser } from './debug';
import { getLocal } from '@shared/components/localStorage';
import store, { history } from '../../pc/models/store';
import { TimeService, EventBus } from '@shared/service';

const isQuiet = isDevelopment() && isQuietMode();
const axiosOptions = {};

const customHeaders = {
  'Cache-Control': 'no-cache',
};

let requestId = 1;
const instance = axios.create({
  baseURL: `${config.baseURL}`,
  timeout: 60 * 1000,
  headers: customHeaders,
  withCredentials: true,
  adapter: (config) => {
    if (config.mock && process.env.NODE_ENV !== 'production') {
      // 模拟服务，返回mock数据
      return new Promise((resolve) => {
        // 调用响应函数
        resolve({
          config: { ...config },
          data: Mock.mock(config.response),
        });
      });
    }
    return cacheAdapterEnhancer(axios.defaults.adapter, { enabledByDefault: false, cacheFlag: 'useCache' })(config);
  },
});

/**
 * 缓存一些全局配置
 * @param {Object} options 格式已axios定义为准，然后做merge操作
 */
function setOptions(options) {
  Object.assign(axiosOptions, options);
}

// 这里在request前附加这些参数
instance.interceptors.request.use((AxiosRequestConfig) => {
  // create时可能还没配置axiosOptions，所以这里动态设置
  // config.headers = Object.assign({}, config.headers, axiosOptions.headers)
  // config = Object.assign({}, axiosOptions, config)
  // create时可能还没配置axiosOptions，所以这里动态设置
  const config = AxiosRequestConfig;
  config.headers = Object.assign(config.headers, axiosOptions.headers);

  // 当token存在时，请求header中携带token
  if (getLocal('TOKEN')) {
    config.headers = Object.assign(config.headers, {
      Authorization: `Bearer ${getLocal('TOKEN')}` || '',
    });
  }

  config.rawParams = ['put', 'post'].includes(config.method) ? { ...config.data } : { ...config.params };
  // 开发测试调试用
  if (isDebug() && isProjectUrl(config.url) && config.url.indexOf('ghostlogin=') === -1) {
    config.params = Object.assign(
      {},
      {
        ghostlogin: getGhostUser(),
      },
      config.params,
    );
  }

  let { data } = config;

  if (config.method === 'post') {
    const formType = 'application/json';
    const contentType = config.headers.post['Content-Type'];

    // 自动将带File对象的data参数转换为FormData对象
    if (!(data instanceof FormData)) {
      const flatObj = flatObject(data);
      const hasFile = Object.values(flatObj).find((v) => v instanceof File);
      if (hasFile) {
        const formData = new FormData();
        Object.keys(flatObj).forEach((key) => {
          const value = flatObj[key];

          // FormData形式传入null会变成字符串
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });

        data = formData;
        config.data = data;
      }
    }

    if (data instanceof FormData && contentType.indexOf(formType) !== -1) {
      config.headers.post['Content-Type'] = 'multipart/form-data';
    }

    // application/x-www-form-urlencoded格式
    else if (contentType.indexOf(formType) !== -1) {
      config.data = qs.stringify(data);
    }
  }
  requestId += 1;
  config._rid = requestId;
  isQuiet || logger.logRequest({ config, params: config.rawParams, rid: config._rid });

  return config;
});

instance.interceptors.response.use(
  (response) => {
    // 业务API响应，非10000都当做异常; 存在code即认为是业务API响应格式，保留__ignore参数忽略次处理逻辑
    // 其他响应，直接返回
    //
    // 存在_ignore参数，则不处理响应格式
    // 适用于自定义JSON等一些场景，例如App version.json

    // 更新时间
    if (response?.headers?.date) {
      TimeService.updateTime(response?.headers?.date);
    }
    const params = response.config.rawParams;
    const ignoreResponse = !params || params.__ignore === undefined;
    const responseData = response.data;

    if (
      typeof responseData === 'object' &&
      responseData !== null &&
      responseData.code !== undefined &&
      ignoreResponse
    ) {
      const { code, data, info } = responseData;
      if (code === 1 || code === 10000) {
        // 如果是mock打印mock的日志
        if (response?.config?.mock) {
          isQuiet ||
            logger.logMockResponse({ config: response.config, params: { ...params }, rid: response.config._rid, data });
        } else {
          isQuiet ||
            logger.logResponse({ config: response.config, params: { ...params }, rid: response.config._rid, data });
        }
        return Promise.resolve(data);
      }
      if (code === 10004) {
        EventBus.emit('permissionDenied', { content: '内部错误，请联系您的投资经理反馈。(10004)' });
      }
      isQuiet ||
        logger.logErrorResponse({
          config: response.config,
          params: { ...params },
          rid: response.config._rid,
          data: info,
        });
      return Promise.reject(info);
    }
    return Promise.resolve(responseData);
  },
  (e) => {
    // 这里基于pc暂时做拦截处理
    const {
      response: { status },
    } = e;
    const { dispatch } = store;
    if (status === 401) {
      history.push('/login');
      dispatch.auth.logout();
      // // 301有可能造成401，二次检查
      // const header = {
      //   ...customHeaders,
      //   Authorization: `Bearer ${getLocal('TOKEN')}` || '',
      // };
      // axios
      //   .request({
      //     baseURL: `${config.baseURL}`,
      //     timeout: 60 * 1000,
      //     headers: header,
      //     withCredentials: true,
      //     url: 'api/v1/users/me',
      //   })
      //   .then(
      //     (res) => {
      //       const { status } = res;
      //       console.log(222);
      //       if (status === 401) {
      //         history.push('/login');
      //         dispatch.auth.logout();
      //       }
      //     });
    }
    const error = new Error(i18n.t('shared-common:request fail', { message: e.message }));
    return Promise.reject(error);
  },
);

export { setOptions, instance };
